import { type FunctionComponent, Fragment } from 'react';
import { PAGE_CONTENT_ELEMENT } from '../../../constants/general';
import { usePagedSearchParams } from '../../../hooks/paged-search-params/paged-search-params.hooks';
import { type ArticleSearch } from '../../../types/article.types';
import { scrollToElement } from '../../../utils/dom/dom.utils';
import { RelatedArticle } from '../../construct-components/related-article/related-article.component';
import { Pagination } from '../../inputs/pagination/pagination.component';
import { LearningCenterEmailTile } from '../learning-center-email-tile/learning-center-email-tile.component';

const SCROLL_OFFSET = 15;
const DEFAULT_EMAIL_CARD_INDEX = 5;

const getNewPageForPageSizeChange = (page: number, pageSize: number, newPageSize: number): number => {
	return Math.floor((pageSize * (page - 1)) / newPageSize + 1);
};

const scrollToHeader = () => {
	scrollToElement(PAGE_CONTENT_ELEMENT, SCROLL_OFFSET);
};

export type LearningCenterArticlesProps = Pick<ArticleSearch, 'count' | 'articles'>;

export const LearningCenterArticles: FunctionComponent<LearningCenterArticlesProps> = ({ count, articles }) => {
	const { offset: page, limit: pageSize, update } = usePagedSearchParams();
	const numPages = Math.ceil(count / pageSize);
	// adjust count to include injected email cards
	const totalWithEmailCards = count + numPages;

	const setPageSize = (newPageSize: number) => {
		const newPage = getNewPageForPageSizeChange(page, pageSize, newPageSize);
		update(newPage, newPageSize);
	};

	const setPage = (newPage: number) => {
		update(newPage, pageSize);
	};

	const emailCardIndex = articles?.length >= DEFAULT_EMAIL_CARD_INDEX ? DEFAULT_EMAIL_CARD_INDEX : articles.length - 1;

	return (
		<div className="pa3">
			<div className="dn ph3 pb2 pb0-l flex-l">
				<div className="flex-grow-1">
					<span className="f5">{count} Articles</span>
				</div>
			</div>
			{articles.length ? (
				<>
					<div className="flex flex-wrap justify-start w-100 items-center mt2 mb3">
						{articles.map((article, index) =>
							index === emailCardIndex ? (
								<Fragment key={article.id}>
									<LearningCenterEmailTile />
									<RelatedArticle {...article} />
								</Fragment>
							) : (
								<RelatedArticle key={article.id} {...article} />
							)
						)}
					</div>
					<div className="inline-block pa3 w-100">
						<Pagination
							pageSize={pageSize}
							setPageSize={setPageSize}
							page={page}
							setPage={setPage}
							totalCount={totalWithEmailCards}
							onChange={scrollToHeader}
						/>
					</div>
				</>
			) : null}
		</div>
	);
};
